import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
// MATERIAL-UI
import Button from '@mui/material/Button';
import { ZuiIcon } from 'components/Zui/ZuiIcon';
import Search from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';
import SaveIcon from '@mui/icons-material/Save';
import { Badge, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// HOOKS
import { useToggle } from 'hooks/react-query/useToggle';
// COMPONENTS
import AccountMenu from './AccountMenu';
import ToolsMenu from './ToolsMenu';
// CONSTANTS
import { moduleConfig, MIEDGE_ALLOW_STATISTICS } from 'lib/config/appConstants';
// STYLE & ICONS
import { white, miEdgeOrange } from 'styles/miEdgeColors';

const styles = (theme) => ({
  icon: {
    color: white,
  },
  buttonText: {
    color: white,
  },
  iconAlerts: {
    color: white,
    height: '18px',
    width: '18px',
    marginRight: '2px',
    marginTop: '-3px',
  },
  iconNavOrganization: {
    marginRight: '10px',
    color: white,
  },
  badge: {
    margin: 0,
  },
  badgeStyle1: {
    margin: 0,
    paddingRight: '15px',
  },
  badgeStyle2: {
    margin: 0,
    paddingRight: '10px',
  },
  badgeStyle3: {
    margin: 0,
    paddingRight: '8px',
  },
  badgeBadge: {
    // added to prevent an issue where badge colors were reverting to pink on resize
    backgroundColor: miEdgeOrange[500],
    fontWeight: 400,
  },
  badgeColorSecondary: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: '10px',
  },
});

const RightAppBar = (props) => {
  if (!props || !props.userInfo) {
    return null;
  }

  const { module, onAppError, userInfo, alertsCount, onDetailViewOpen } = props;
  const theme = useTheme();
  const sx = styles(theme);

  // get toggle for stats page with useToggle hook
  const {
    data: statsToggle,
    isLoading: isStatsToggleLoading,
    isError: isStatsToggleError,
  } = useToggle(MIEDGE_ALLOW_STATISTICS);

  const allowStatistics = statsToggle && !isStatsToggleLoading && !isStatsToggleError;

  const alertsLabel =
    alertsCount === 0 ? (
      'Alerts'
    ) : (
      <Badge
        badgeContent={alertsCount}
        sx={{
          ...(alertsCount > 99
            ? sx.badgeStyle1
            : alertsCount > 9 && alertsCount <= 99
              ? sx.badgeStyle2
              : alertsCount > 0 && alertsCount <= 9
                ? sx.badgeStyle3
                : sx.badge),
          '& .MuiBadge-root': sx.badgeBadge,
          '& .MuiBadge-badge': sx.badgeColorSecondary,
        }}
        color="secondary"
      >
        Alerts
      </Badge>
    );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Button
        component={Link}
        to={module === moduleConfig.PC.shortName ? moduleConfig.PC.home : moduleConfig.EB.home}
      >
        <Search sx={sx.icon} />
        <Typography sx={sx.buttonText} variant="button">
          Search
        </Typography>
      </Button>

      <Button
        component={Link}
        to={`${
          module === moduleConfig.PC.shortName ? moduleConfig.PC.home : moduleConfig.EB.home
        }/savedsearches`}
      >
        <SaveIcon sx={sx.icon} />
        <Typography sx={sx.buttonText} variant="button">
          Saved Searches
        </Typography>
      </Button>

      <Button component={Link} to={'/edge/' + module.toLowerCase() + '/favorites'}>
        <ListIcon sx={sx.icon} />
        <Typography sx={sx.buttonText} variant="button">
          Favorites
        </Typography>
      </Button>
      <Button
        component={Link}
        to={'/edge/' + module.toLowerCase() + '/event_alerts'}
        onClick={() => {
          if (onDetailViewOpen) {
            onDetailViewOpen(false);
          }
        }}
      >
        <ZuiIcon unresolved icon="zui-nav-notifications" slot="icon" style={sx.iconAlerts} />
        <Typography sx={sx.buttonText} variant="button">
          {alertsLabel}
        </Typography>
      </Button>
      <ToolsMenu
        onAppError={onAppError}
        userInfo={userInfo}
        module={module}
        allowStatistics={allowStatistics}
      />
      <AccountMenu onAppError={onAppError} style={{ color: white }} userInfo={userInfo} />
    </div>
  );
};

RightAppBar.defaultProps = {
  alertsCount: 0,
};

RightAppBar.propTypes = {
  userInfo: PropTypes.object,
  alertsCount: PropTypes.number,
  onAppError: PropTypes.func.isRequired,
  module: PropTypes.oneOf([moduleConfig.PC.shortName, moduleConfig.EB.shortName]),
  onDetailViewOpen: PropTypes.func,
};

export default RightAppBar;
